/**
 * InboxPage — turns on which the coach recorded tasks for an RM.
 *
 * Three things this page must say out loud, because each of them makes the
 * numbers mean something other than what they look like, and none of the three
 * is visible from the numbers alone:
 *
 *   1. WHERE THE DATA STARTS. A turn's type is not derivable before
 *      2026-08-18 — the metadata did not exist. Older turns are not "coach
 *      turns", they are turns of unknown type, and they are absent here. The
 *      date is the one the API sends, never a copy hard-coded here.
 *   2. HOW MANY TURNS ARE RATED. An unrated turn earns 0.3 of its minutes,
 *      so while turns go unrated Time saved rests on a signal nobody has sent.
 *      What arriving ratings would do is NOT a known multiple: the weights
 *      (queries.ts:215-222) are 5 -> 1.0, 4 -> 0.7, 3 -> 0.3, 1-2 -> 0, so a 3
 *      changes nothing against today's 0.3 and a 1-2 makes the figure SMALLER.
 *      Any single multiple quoted here would be the best case worn as a fact.
 *
 *      The note lives directly under the Time saved tile, in the flow of the
 *      page, never a tooltip — and it renders IF AND ONLY IF at least one
 *      unrated capture turn exists. That one condition is the whole gate: it
 *      is a claim about unrated turns, so no unrated turns means no claim.
 *      Gating on anything else (`ratedTurns === 0` was the earlier attempt)
 *      leaves the card asserting a discount over an empty set — with every
 *      turn rated, or with no capture turns at all.
 *   3. WHOSE TURNS ARE COUNTED. Traces carrying an admin's Slack id are dropped
 *      before any number here is computed — `buildFilterSQL`'s `excludeAdmins`,
 *      which `mapToTraceFilters` derives from the global Admins filter for
 *      every tab route, so the Inbox counts the same population as its
 *      neighbours. It matters most here: the busiest capture user on the
 *      mirror IS an admin, and dropping him took the refusal share from 29%
 *      over every row to the 67% this page showed (2026-08-19). No figure on
 *      screen can tell the reader that.
 *
 *      TWO SENTENCES, one per state of that filter, since the toggle landed.
 *      The rule is no longer a constant, so a single sentence would be false
 *      half the time — and the half it would be false in is the one the reader
 *      switched INTO precisely because he doubted the numbers. The state is
 *      read from the URL (`useUrlFilters`), never from the payload: it is what
 *      was ASKED for, so it is known before any response exists.
 *
 *      TWO HALVES, because they are true at different moments. The RULE half
 *      ("admin Slack accounts are included/excluded, here as on every other
 *      tab") is UNGATED, unlike the two items above, and for exactly the reason
 *      they are gated: it is about the rule, not about a quantity. It stays
 *      true before the response lands, on an empty window, and in a window
 *      where the rule happened to drop nothing. The count form ("N admin turns
 *      excluded") would be a claim about a number the payload does not carry —
 *      and adding it would buy a fifth chance to print a false one.
 *
 *      The SECOND half is a claim about the counts on screen. It used to carry
 *      a THIRD sentence for the window in which those counts belonged to the
 *      previous selection — `keepPreviousData` kept them on screen through a
 *      filter change, so the page was saying the admins were counted over
 *      numbers that excluded them (browser-check 2026-08-19, against a slowed
 *      API). That sentence is GONE because that window is gone: since the
 *      recount rule (hooks/use-filtered-query.ts) the previous selection's
 *      counts are never on screen at all, and a sentence about "the counts
 *      below" while they are skeletons would be the next untrue one. What runs
 *      instead is what runs on every tab — the values leave and
 *      <RecountNotice> says a recount is running.
 *
 *      It also stays true when `ADMIN_SLACK_IDS` is unset and `buildFilterSQL`
 *      emits no admin predicate at all: the named population is then empty, so
 *      both sentences hold vacuously — excluding nobody and including nobody
 *      are the same window, and neither sentence claims anyone was dropped.
 *
 *      SLACK ACCOUNTS, not "admins": the predicate is `slack_id IS NULL OR
 *      slack_id NOT IN (admins)` (filters.ts), and a web turn carries no Slack
 *      id at all — `coach-http/routes.ts` sends none — so an admin's WEB turns
 *      ARE counted here IN BOTH STATES. "Admin turns are excluded" is the
 *      shorter sentence and the untrue one; so is "now you see the admins",
 *      which is why the Included sentence says what it says.
 *
 * Filters come from the global FilterBar via DashboardLayout — no per-tab
 * widgets, same as every other tab.
 */

import * as React from 'react';

import { KPICard } from '@/components/KPICard';
import { RecountNotice } from '@/components/RecountNotice';
import { SortableTable, type SortableColumn } from '@/components/SortableTable';
import { TabErrorBoundary } from '@/components/TabErrorBoundary';
import { Card, CardContent } from '@/components/ui/card';
import { RoleBadge } from '@/components/ui/role-badge';
import { UserLabel } from '@/components/ui/user-label';
import { useInboxKpis } from '@/hooks/use-inbox';
import { useOpenUserDetail } from '@/hooks/use-open-user-detail';
import { useUrlFilters } from '@/hooks/use-url-filters';
import type { InboxUserRow } from '@/services/api-types';

/** A mean DERIVED from two counts the page renders as tiles of its own — the
 *  API sends no computed field at all, so the mean and its inputs are the same
 *  arithmetic by construction.
 *
 *  Nothing to divide by renders as an em-dash, never as 0 and never as `NaN`:
 *  "no turns" and "zero tasks per turn" are different facts. */
function ratio(
  numerator: number | undefined,
  denominator: number | undefined,
): string {
  if (!denominator) return '—';
  return ((numerator ?? 0) / denominator).toFixed(1);
}

/** A share DERIVED from the two counts the page prints beside it — the API
 *  sends no share of its own, so the percentage and the counts on screen are
 *  the same arithmetic by construction and cannot disagree.
 *
 *  A zero (or absent) denominator is "we counted nothing", which is neither
 *  `0%` (a real share that happens to be zero) nor `NaN%`. It renders as the
 *  same em-dash every other missing ratio uses. */
function share(
  numerator: number | undefined,
  denominator: number | undefined,
): string {
  if (!denominator) return '—';
  return `${(((numerator ?? 0) / denominator) * 100).toFixed(0)}%`;
}

/** `format` (not `render`) is SortableColumn's real cell hook, and it receives
 *  the raw cell value plus the row.
 *
 *  THE FIRST THREE COLUMNS ARE ENGAGEMENT'S, component for component
 *  (`EngagementPage.tsx`): `UserLabel` for the name, a bare `@handle` for the
 *  user, `RoleBadge` for the role. Rendering the same three facts a second way
 *  is the failure this copies its way out of — the two tables are read side by
 *  side and have already had to be reconciled twice.
 *
 *  THE FOUR NUMBERS ARE THE INBOX'S OWN and are untouched: same keys, same
 *  right alignment, same hours-to-one-decimal for the minutes the API sends. */
const USER_COLUMNS: Array<SortableColumn<InboxUserRow>> = [
  {
    key: 'realName',
    header: 'Name',
    format: (_v, r) => (
      <UserLabel name={r.realName} username={r.userId} variant="cell" />
    ),
  },
  {
    key: 'userId',
    // A null userId keeps the em-dash it already had: `@` alone would read as
    // a handle, and Engagement never meets this row (it drops null users).
    header: 'User',
    format: (_v, r) => (r.userId ? `@${r.userId}` : '—'),
  },
  { key: 'role', header: 'Role', format: (_v, r) => <RoleBadge role={r.role} /> },
  { key: 'captureTurns', header: 'Turns', align: 'right' },
  { key: 'tasksCreated', header: 'Tasks created', align: 'right' },
  { key: 'tasksRefused', header: 'Refused', align: 'right' },
  {
    key: 'timeSavedMinutes',
    header: 'Time saved',
    align: 'right',
    format: (_v, r) => `${(r.timeSavedMinutes / 60).toFixed(1)}h`,
  },
];

export function InboxPage(): React.ReactElement {
  // The SAME opener Engagement's rows use, so a person reached from here and
  // the same person reached from there land on one page in one state.
  const { openUserDetail } = useOpenUserDetail();
  const { filters } = useUrlFilters();
  const inbox = useInboxKpis(filters);
  const { data, isLoading, isError, refetch } = inbox;
  const rows = data?.byUser ?? [];
  /* The paragraph below the rated-share counts is a claim about UNRATED turns,
     so this count — not the rated one — decides whether it may be said at all. */
  const unratedTurns = data ? data.captureTurns - data.ratedTurns : 0;

  /* The admin caption, in two halves — see 3. in the header comment.

     THE RULE half is read from the URL, so it is known before any response
     exists and is true in every payload state.

     THE CLAIM ABOUT THE NUMBERS is only true once the numbers below were
     produced by that rule. It used to need a third branch on
     `isPlaceholderData`, for the window in which `keepPreviousData` left the
     previous selection's population on screen. That window no longer exists:
     while the recount runs there are no counts below to make a claim about,
     the tiles and the table are skeletons, and <RecountNotice> says so. */
  const adminsIncluded = filters.admins === 'included';
  const captionRule = adminsIncluded
    ? 'Turns from admin Slack accounts are included, here as on every other tab'
    : 'Turns from admin Slack accounts are excluded, here as on every other tab';
  const captionNumbers = adminsIncluded
    ? " — every count and share below counts them alongside everyone else's."
    : ' — every count and share below is over the turns that remain.';

  /* Engagement's `handleRowClick`, guard included — and here the guard is not
     defensive: this table KEEPS the anonymous row (Engagement drops null
     users), and there is no user page for nobody. */
  const handleRowClick = React.useCallback(
    (row: InboxUserRow) => {
      if (row.userId) openUserDetail(row.userId);
    },
    [openUserDetail],
  );

  if (isError) {
    return (
      <Card>
        <CardContent className="p-4 space-y-2">
          <div className="text-sm font-medium text-red-600">
            Inbox failed to load
          </div>
          <button
            type="button"
            onClick={() => void refetch()}
            className="text-xs underline text-primary hover:no-underline"
          >
            Retry
          </button>
        </CardContent>
      </Card>
    );
  }

  return (
    <TabErrorBoundary name="Inbox">
      <section aria-labelledby="inbox-heading" className="space-y-4">
        <header>
          <h1 id="inbox-heading" className="text-2xl font-semibold">
            Inbox
          </h1>
          {/* The date comes from the API, so before a response there is no
              date to print — and "Data from —." reads as a fact about the
              window rather than as its absence. Same skeleton idiom the tiles
              use, so the line keeps its height. */}
          <p
            className="text-sm text-muted-foreground"
            data-testid="inbox-data-from"
          >
            {data ? (
              <>
                Data from {data.dataFrom}. Turns before this date carry no type
                — they are absent here, not counted as zero.
              </>
            ) : (
              <span
                data-testid="inbox-data-from-skeleton"
                className="inline-block h-4 w-80 max-w-full rounded bg-muted animate-pulse align-middle"
              />
            )}
          </p>
          {/* Two halves, composed above, both now unconditional: the RULE
              comes from the URL, so it is as true of a window that is still
              counting, or empty, or that held no admin turn to drop, as of
              the live one — and the CLAIM ABOUT THE NUMBERS no longer needs a
              gate, because during a recount there are no numbers below to
              describe. See 3. in the header comment. */}
          <p
            className="text-sm text-muted-foreground"
            data-testid="inbox-admin-note"
          >
            {captionRule}
            {captionNumbers}
          </p>
          <RecountNotice queries={[inbox]} />
        </header>

        <div className="grid gap-3 grid-cols-2 lg:grid-cols-5">
          <KPICard
            label="Capture turns"
            value={data?.captureTurns ?? null}
            format="number"
            loading={isLoading}
            showDelta={false}
          />
          <KPICard
            label="Tasks created"
            value={data?.tasksCreated ?? null}
            format="number"
            loading={isLoading}
            showDelta={false}
          />
          <KPICard
            label="Tasks per turn"
            value={ratio(data?.tasksCreated, data?.captureTurns)}
            loading={isLoading}
            showDelta={false}
          />
          <KPICard
            label="Refused"
            value={share(data?.tasksRefused, data?.taskRecords)}
            subLabel={
              data && `${data.tasksRefused} of ${data.taskRecords} task records`
            }
            loading={isLoading}
            showDelta={false}
            tooltip="Share of attempted task writes Mr. Edge declined. Tasks dropped before dispatch leave no record and are neither counted here nor reported as lost."
          />
          <KPICard
            label="Time saved"
            value={data ? data.timeSavedMinutes / 60 : null}
            format="hours"
            loading={isLoading}
            showDelta={false}
          />
        </div>

        <Card data-testid="inbox-rated-share">
          <CardContent className="p-4 text-sm">
            {data ? (
              <span className="font-medium">
                {data.ratedTurns} of {data.captureTurns} capture turns rated (
                {share(data.ratedTurns, data.captureTurns)})
              </span>
            ) : (
              /* "0 of 0 rated" before the response lands is a count, and it is
                 wrong. Same skeleton idiom KPICard uses, so the card keeps its
                 height and the page does not jump when data arrives. */
              <div
                data-testid="inbox-rated-share-skeleton"
                className="h-5 w-64 rounded bg-muted animate-pulse"
              />
            )}
            {/* ONE sentence, phrased around the quantity it is about: the
                unrated turns. Rendered only when there is at least one, which
                is simultaneously the right condition for "no capture turns at
                all" and for "every turn already rated" — in both of those the
                paragraph would be describing a discount over an empty set.
                A second branch would only be a second place for an untrue
                sentence to hide; the weights read the same either way. */}
            {unratedTurns > 0 && (
              <p
                className="text-xs text-muted-foreground mt-1"
                data-testid="inbox-unrated-note"
              >
                {unratedTurns} of the {data?.captureTurns} capture turns above{' '}
                {unratedTurns === 1 ? 'is' : 'are'} unrated, each credited at
                0.3 of its minutes — the same discount every unrated Mr. Edge turn
                takes. Rating them would credit each by its score instead: a 5
                credits the full minutes, a 4 credits 0.7, a 3 lands exactly
                where unrated already sits, and a 1 or 2 credits nothing at all.
                So Time saved above can move either way as ratings arrive.
              </p>
            )}
          </CardContent>
        </Card>

        <SortableTable
          tableId="inbox-users"
          rows={rows}
          columns={USER_COLUMNS}
          /* A null userId is a real possibility and more than one row can carry
             it; keying them all '(unknown)' collides, which is how React starts
             reusing one row's DOM for another. Position disambiguates. */
          getRowKey={(r) => r.userId ?? `(unknown-${rows.indexOf(r)})`}
          onRowClick={handleRowClick}
          isLoading={isLoading}
          emptyMessage="No capture turns in selected period"
        />
      </section>
    </TabErrorBoundary>
  );
}
